"use client";

import { EditableValue } from "../forms/EditableValue";

/* -------------------------------------------------------------------------- */
/* Types                                                                       */
/* -------------------------------------------------------------------------- */

export interface MixEntry {
  id: string;
  label: string;
  sharePct: number;
}

export interface MixSectionProps {
  /** The list of mix entries (payers or specialties). */
  entries: MixEntry[];
  /**
   * Pre-computed sum of all `sharePct` values.
   * Pass it in so the component stays purely presentational.
   */
  total: number;
  /** Label for the "add" button (e.g. `"Add payer"`). */
  addLabel: string;
  /**
   * Noun used in the status message (e.g. `"Payer mix"` → "Payer mix sums to 100%").
   */
  itemNoun: string;
  onSetLabel: (id: string, label: string) => void;
  onSetShare: (id: string, sharePct: number) => void;
  onRemove: (id: string) => void;
  onAdd: () => void;
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

type Tone = "ok" | "over" | "under";

function getTone(total: number): Tone {
  if (Math.abs(total - 100) < 0.05) return "ok";
  if (total > 100) return "over";
  return "under";
}

const TONE_CLS: Record<Tone, { wrapper: string; fill: string; text: string }> =
  {
    ok: {
      wrapper:
        "border-[color-mix(in_oklab,var(--accent-teal)_35%,transparent)] bg-[color-mix(in_oklab,var(--accent-teal)_6%,transparent)]",
      fill: "bg-[color-mix(in_oklab,var(--accent-teal)_60%,transparent)]",
      text: "text-[var(--accent-teal-text,var(--accent-teal))]",
    },
    over: {
      wrapper:
        "border-[color-mix(in_oklab,var(--accent-red,#e03)_35%,transparent)] bg-[color-mix(in_oklab,var(--accent-red,#e03)_6%,transparent)]",
      fill: "bg-[color-mix(in_oklab,var(--accent-red,#e03)_55%,transparent)]",
      text: "text-[var(--accent-red-text,var(--fg-danger,#c00))]",
    },
    under: {
      wrapper:
        "border-[color-mix(in_oklab,var(--accent-gold)_35%,transparent)] bg-[color-mix(in_oklab,var(--accent-gold)_6%,transparent)]",
      fill: "bg-[color-mix(in_oklab,var(--accent-gold)_55%,transparent)]",
      text: "text-[var(--accent-gold-text,var(--fg-secondary))]",
    },
  };

function statusMessage(tone: Tone, total: number, itemNoun: string): string {
  if (tone === "ok") return `${itemNoun} sums to 100%. Ready to save.`;
  if (tone === "over")
    return `Over by ${(total - 100).toFixed(1)}%. Reduce shares so the total equals 100%.`;
  return `${(100 - total).toFixed(1)}% remaining. Add shares so the total equals 100%.`;
}

/* -------------------------------------------------------------------------- */
/* MixSection                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Combined payer-mix / specialty-mix editor.
 *
 * Renders a percentage-total indicator bar followed by an editable list of
 * mix entries.  Tone (ok / over / under) is derived from the `total` prop.
 */
export function MixSection({
  entries,
  total,
  addLabel,
  itemNoun,
  onSetLabel,
  onSetShare,
  onRemove,
  onAdd,
}: MixSectionProps) {
  const tone = getTone(total);
  const cls = TONE_CLS[tone];
  const fillPct = Math.min(100, total);
  const overflowPct = total > 100 ? Math.min(100, total - 100) : 0;

  return (
    <div className="flex flex-col gap-4">
      {/* ── Total indicator ──────────────────────────────────────────────── */}
      <div
        role="status"
        className={[
          "rounded-[6px] border px-4 py-3 flex flex-col gap-2",
          cls.wrapper,
        ].join(" ")}
      >
        {/* Head row */}
        <div className="flex items-center justify-between text-sm font-medium">
          <span className="text-(--fg-secondary)">Total</span>
          <span className="tabular-nums">
            <span className={cls.text}>{total.toFixed(1)}%</span>
            <span className="text-(--fg-muted) ml-1">/ 100%</span>
          </span>
        </div>

        {/* Progress bar */}
        <div
          aria-hidden
          className="relative h-[6px] rounded-full bg-(--bg-canvas) overflow-hidden"
        >
          <div
            className={`absolute inset-y-0 left-0 rounded-full transition-[width] duration-200 ${cls.fill}`}
            style={{ width: `${fillPct}%` }}
          />
          {overflowPct > 0 && (
            <div
              className="absolute inset-y-0 right-0 rounded-full bg-[color-mix(in_oklab,var(--accent-red,#e03)_55%,transparent)]"
              style={{ width: `${overflowPct}%` }}
            />
          )}
        </div>

        {/* Status message */}
        <p className={`text-xs ${cls.text}`}>
          {statusMessage(tone, total, itemNoun)}
        </p>
      </div>

      {/* ── Entry list ───────────────────────────────────────────────────── */}
      <ul className="divide-y divide-(--border-default)">
        {entries.map((entry) => (
          <li key={entry.id} className="flex items-center gap-2 py-2 group/row">
            {/* Label */}
            <span className="flex-1 text-sm text-(--fg-primary) min-w-0">
              <EditableValue
                value={entry.label}
                onSave={(v) => onSetLabel(entry.id, v.trim() || entry.label)}
              />
            </span>

            {/* Share % */}
            <span className="text-sm text-(--fg-primary) tabular-nums">
              <EditableValue
                value={entry.sharePct.toFixed(1)}
                align="right"
                type="number"
                suffix="%"
                onSave={(v) => {
                  const n = parseFloat(v);
                  if (!Number.isNaN(n)) onSetShare(entry.id, n);
                }}
              />
            </span>

            {/* Remove button */}
            <button
              type="button"
              onClick={() => onRemove(entry.id)}
              aria-label={`Remove ${entry.label}`}
              className={[
                "p-1 rounded text-(--fg-muted)",
                "opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100",
                "hover:text-(--fg-danger,var(--accent-red,#c00))",
                "transition-opacity",
              ].join(" ")}
            >
              <svg
                aria-hidden
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 6l12 12M18 6l-12 12" />
              </svg>
            </button>
          </li>
        ))}
      </ul>

      {/* ── Add button ───────────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={onAdd}
        className={[
          "inline-flex items-center gap-1.5 self-start",
          "text-sm text-(--fg-secondary)",
          "px-2 py-1 rounded hover:bg-(--bg-canvas)",
          "transition-colors",
          "focus-visible:outline-2 focus-visible:outline-[var(--fg-brand)] focus-visible:outline-offset-1",
        ].join(" ")}
      >
        <svg
          aria-hidden
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        {addLabel}
      </button>
    </div>
  );
}
